import { HTTP_INTERCEPTORS, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpHandler, HttpRequest } from '@angular/common/http';

import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { UserService, SentryService } from '../services';
import { KindeAuthService } from '../services/kinde-auth.service';
import { ACCESS_TOKEN_KEY, ERP_EXCHANGE_TOKEN_URLS } from 'app/shared/constants';
import { AppNotify } from './notification-helper';
import { AppStorage } from './storage';


const TOKEN_HEADER_KEY = 'Authorization';  // for Spring Boot back-end

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  constructor(
    private userService: UserService,
    private sentryService: SentryService,
    private kindeAuth: KindeAuthService,
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<Object>> {
    // Skip token attachment for ERP OAuth exchange endpoints
    if (ERP_EXCHANGE_TOKEN_URLS.indexOf(req.url) >= 0) {
      return next.handle(req).pipe(
        catchError(error => {
          if (error instanceof HttpErrorResponse) {
            return this.handleOtherErrors(error);
          }
          return throwError(() => error);
        })
      );
    }

    // Bridge code auth (admin impersonation) stores SZ tokens directly — use those
    // when available instead of Kinde tokens. This avoids calling Kinde SDK
    // when it was never initialized.
    const szToken = AppStorage.getTokenData(ACCESS_TOKEN_KEY);
    const tokenSource$ = szToken
      ? from(Promise.resolve(szToken))
      : from(this.kindeAuth.getToken());

    return tokenSource$.pipe(
      switchMap(token => {
        const authReq = token
          ? this.addTokenHeader(req, token)
          : req;
        return next.handle(authReq).pipe(
          catchError(error => {
            if (error instanceof HttpErrorResponse) {
              if (error.status === 401) {
                // Token rejected — redirect to login
                this.userService.navigateToLogin();
                return throwError(() => error);
              }
              return this.handleOtherErrors(error);
            }
            return throwError(() => error);
          })
        );
      })
    );
  }

  private handleOtherErrors(response: HttpErrorResponse) {
    // Cancelled request, this error happen after user view document
    if (response.status === 0) {
      const messageTimeout = 'The request timed out. Please check your connection and try again.';
      let error = response.error ? (response.error.message || messageTimeout) : response.statusText;
      if (!error) {
        error = 'Unable to reach the server. Please try again.';
      }

      // Capture timeout errors in Sentry
      this.sentryService.captureHttpError(
        new Error(error),
        response.url || 'unknown',
        response.status,
        { component: 'AuthInterceptor', method: 'handleOtherErrors' }
      );

      AppNotify.error(error, 5000);
      return throwError(() => response);
    }

    // Handle 429 Too Many Requests
    if (response.status === 429) {
      const errorMessage = "You're performing actions too quickly. Please wait a moment before trying again.";

      // Show non-blocking notification
      AppNotify.warning(errorMessage, 5000);

      // Don't capture in Sentry as this is expected rate limiting behavior
      return throwError(() => new Error(errorMessage));
    }

    // Handle 404 Not Found
    if (response.status === 404) {
      // Don't show toast for 404s — they're expected for missing resources
      // Just log to Sentry for monitoring
      this.sentryService.captureHttpError(
        new Error('Resource not found'),
        response.url || 'unknown',
        response.status,
        { component: 'AuthInterceptor', method: 'handleOtherErrors' }
      );
      return throwError(() => response);
    }

    // Handle 403 Forbidden
    if (response.status === 403) {
      let errorMessage = "You don't have permission to perform this action.";
      if (!!response.error && !!response.error.message) {
        errorMessage = response.error.message;
      } else if (!!response.error && !!response.error.title) {
        errorMessage = response.error.title;
      }

      // Capture permission denied errors in Sentry
      this.sentryService.captureHttpError(
        new Error(errorMessage),
        response.url || 'unknown',
        response.status,
        { component: 'AuthInterceptor', method: 'handleOtherErrors' }
      );

      AppNotify.error(errorMessage);
      return throwError(() => 'Permission Denied');
    }

    // Handle 413 Payload Too Large
    if (response.status === 413) {
      AppNotify.error('The file is too large to upload. Please reduce the file size and try again.', 5000);
      return throwError(() => response);
    }

    // Handle 500 Internal Server Error
    if (response.status === 500) {
      let error = response.error ? response.error.message : response.statusText;
      if (!error) {
        error = 'Internal Server Error';
      } else {
        error = error.split('\r\n')[0];
      }

      // Capture server errors in Sentry (high priority)
      this.sentryService.captureHttpError(
        new Error(error),
        response.url || 'unknown',
        response.status,
        {
          component: 'AuthInterceptor',
          method: 'handleOtherErrors',
          additionalData: { serverError: response.error }
        }
      );

      // Suppress toast for ERP connect errors — handled by ErpConnectComponent with retry
      const isErpConnect = response.url?.includes('/erp/connect');
      if (!isErpConnect) {
        AppNotify.error(error, 5000);
      }
      return throwError(() => response);
    }

    // Handle 402 Payment Required
    if (response.status === 402 && response.statusText === 'Payment Required') {
      // Capture server errors in Sentry (high priority)
      this.sentryService.captureHttpError(
        new Error(response.statusText),
        response.url || 'unknown',
        response.status,
        {
          component: 'AuthInterceptor',
          method: 'handleOtherErrors',
          additionalData: { serverError: response.error }
        }
      );
      AppNotify.warning('Your subscription needs attention. Please check your billing settings.', 5000);
      return throwError(() => response);
    }

    // Handle other errors
    let messageError = '';
    if (response.error instanceof ErrorEvent) {
      // A client-side or network error occurred
      console.error('An error occurred:', response.error.message);
    }
    if (response.error && response.error.errors) {
      const errProps = Object.keys(response.error.errors);
      if (errProps.length > 0) {
        messageError = response.error.errors[errProps[0]][0];
      }
    } else {
      console.error(
        `Backend returned code ${response.status}, ` +
        `body was: ${response.error}`);
    }

    if (!messageError) {
      if (!!response.error && !!response.error.message) {
        messageError = response.error.message;
      } else {
        messageError = 'An unexpected error occurred. Please try again or contact support.';
      }
    }

    // Capture other HTTP errors in Sentry
    this.sentryService.captureHttpError(
      new Error(messageError),
      response.url || 'unknown',
      response.status,
      {
        component: 'AuthInterceptor',
        method: 'handleOtherErrors',
        additionalData: {
          errorDetails: response.error,
          statusText: response.statusText
        }
      }
    );

    AppNotify.error(messageError, 5000);
    return throwError(() => response);
  }

  private addTokenHeader(request: HttpRequest<any>, token: string) {
    /* for Spring Boot back-end */
    return request.clone({ headers: request.headers.set(TOKEN_HEADER_KEY, 'Bearer ' + token) });

    /* for Node.js Express back-end */
    // return request.clone({ headers: request.headers.set(TOKEN_HEADER_KEY, token) });
  }
}

export const authInterceptorProviders = [
  { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true }
];
